
import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Star, FileText, Calendar, Filter, ExternalLink } from 'lucide-react';
import { highlightsService, Highlight } from '@/services/highlights';
import { NoteType } from '@/types/notes';
import { useNavigate } from 'react-router-dom';

interface HighlightsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentSpaceId?: string;
}

const noteTypeLabels: Record<NoteType, string> = {
  note: 'Note',
  quote: 'Quote',
  emotion: 'Emotion',
  behaviour: 'Behaviour',
  pain: 'Pain Point',
  insight: 'Insight',
  opportunity: 'Opportunity'
};

const noteTypeColors: Record<NoteType, string> = {
  note: 'bg-gray-100 text-gray-800',
  quote: 'bg-yellow-100 text-yellow-800',
  emotion: 'bg-pink-100 text-pink-800',
  behaviour: 'bg-blue-100 text-blue-800',
  pain: 'bg-red-100 text-red-800',
  insight: 'bg-green-100 text-green-800',
  opportunity: 'bg-purple-100 text-purple-800'
};

export const HighlightsPanel: React.FC<HighlightsPanelProps> = ({
  isOpen,
  onClose,
  currentSpaceId
}) => {
  const navigate = useNavigate();
  const [allHighlights, setAllHighlights] = useState<Highlight[]>([]);
  const [currentSpaceHighlights, setCurrentSpaceHighlights] = useState<Highlight[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => {
    if (isOpen) {
      loadHighlights();
    }
  }, [isOpen, currentSpaceId]);

  const loadHighlights = async () => {
    setLoading(true);
    try {
      const [allResults, currentResults] = await Promise.all([
        highlightsService.getAllHighlights(),
        currentSpaceId ? highlightsService.getHighlightsBySpace(currentSpaceId) : Promise.resolve([])
      ]);
      
      setAllHighlights(allResults);
      setCurrentSpaceHighlights(currentResults);
    } catch (error) {
      console.error('Error loading highlights:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterHighlights = (highlights: Highlight[]) => {
    if (selectedType === 'all') return highlights;
    return highlights.filter(h => h.type === selectedType);
  };

  const groupHighlightsBySpace = (highlights: Highlight[]) => {
    const grouped = highlights.reduce((acc, highlight) => {
      if (!acc[highlight.noteSpaceId]) {
        acc[highlight.noteSpaceId] = {
          spaceName: highlight.noteSpaceName,
          highlights: []
        };
      }
      acc[highlight.noteSpaceId].highlights.push(highlight);
      return acc;
    }, {} as Record<string, { spaceName: string; highlights: Highlight[] }>);
    
    return Object.entries(grouped);
  };

  const navigateToHighlight = (highlight: Highlight) => {
    navigate(`/space/${highlight.noteSpaceId}`);
    onClose();
  };

  const getTypeStats = (highlights: Highlight[]) => {
    const stats = highlights.reduce((acc, h) => {
      acc[h.type] = (acc[h.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(stats).map(([type, count]) => ({ type, count }));
  };

  const filteredAllHighlights = filterHighlights(allHighlights);
  const filteredCurrentHighlights = filterHighlights(currentSpaceHighlights);
  const groupedHighlights = groupHighlightsBySpace(filteredAllHighlights);
  const allTypeStats = getTypeStats(allHighlights);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:w-[600px] sm:max-w-[600px]">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Highlights Analysis
          </SheetTitle>
          <SheetDescription>
            View and analyze highlighted insights across your interviews
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="all" className="h-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="all">All Interviews ({allHighlights.length})</TabsTrigger>
            <TabsTrigger value="current" disabled={!currentSpaceId}>
              Current Interview ({currentSpaceHighlights.length})
            </TabsTrigger>
          </TabsList>

          {/* Type Filter */}
          <div className="flex flex-wrap gap-2 py-4">
            <Button
              variant={selectedType === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedType('all')}
            >
              All Types
            </Button>
            {allTypeStats.map(({ type, count }) => (
              <Button
                key={type}
                variant={selectedType === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType(type)}
                className="text-xs"
              >
                {noteTypeLabels[type as NoteType]} ({count})
              </Button>
            ))}
          </div>

          <TabsContent value="all" className="flex-1">
            <ScrollArea className="h-[calc(100vh-280px)]">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredAllHighlights.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Star className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No highlights found</p>
                  <p className="text-sm">Star notes in your interviews to see them here</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {groupedHighlights.map(([spaceId, { spaceName, highlights }]) => (
                    <div key={spaceId}>
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <h3 className="font-medium text-gray-900">{spaceName}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {highlights.length} highlights
                        </Badge>
                      </div>
                      <div className="space-y-3 ml-6">
                        {highlights.map((highlight) => (
                          <Card key={highlight.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge 
                                      variant="secondary" 
                                      className={`text-xs ${noteTypeColors[highlight.type as NoteType]}`}
                                    >
                                      {noteTypeLabels[highlight.type as NoteType]}
                                    </Badge>
                                    <div className="flex items-center gap-1 text-xs text-gray-500">
                                      <Calendar className="w-3 h-3" />
                                      {new Date(highlight.createdAt).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-700 leading-relaxed">
                                    {highlight.content}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigateToHighlight(highlight)}
                                  className="shrink-0"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                      <Separator className="mt-6" />
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {currentSpaceId && (
            <TabsContent value="current" className="flex-1">
              <ScrollArea className="h-[calc(100vh-280px)]">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredCurrentHighlights.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Star className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No highlights in this interview</p>
                    <p className="text-sm">Star notes to create highlights</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCurrentHighlights.map((highlight) => (
                      <Card key={highlight.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge 
                              variant="secondary" 
                              className={`text-xs ${noteTypeColors[highlight.type as NoteType]}`}
                            >
                              {noteTypeLabels[highlight.type as NoteType]}
                            </Badge>
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <Calendar className="w-3 h-3" />
                              {new Date(highlight.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {highlight.content}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
